import {
  type Row,
  type RowSelectionState,
  type TableOptions_All,
  type TableOptions_Plugins,
} from '@tanstack/react-table';
import { useCallback } from 'react';

import type {
  DataTableRowSelectionOptions,
  DataTableRowSelectionProps,
} from './row-selection-types.js';
import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import { useDataTableConfigElement } from '../../../hooks/useDataTableConfig.js';
import type { DataTableRowData } from '../../row-data-types.js';
import type { TDataTableFeatures } from '../types/features.types.js';

const fallbackState = {};

/**
 * Configuration hook for the DataTable UserActions feature
 * @internal
 */
export function useRowSelection<TData extends DataTableRowData>(
  props: DataTableRowSelectionProps<TData>,
  // Needs to use TableOptions_all here to work around the UnionToIntersection type not working correctly with plugins.
  // Needs to join the TableOptions_Plugins because right now, they are not part of the TableOptions_all
  options: TableOptions_All<TDataTableFeatures, Exclude<TData, unknown>> &
    TableOptions_Plugins<TDataTableFeatures, Exclude<TData, unknown>>,
): void {
  const configSelectableRows = useDataTableConfigElement('selectableRows');
  // Need to move that into a const here in order to make the typechecks
  // work, because otherwise the selectableRows member could switch.

  // note: for case where the selectableRows prop is an object, it needs
  // to be memoized by the consumer, to avoid updates if it is identical
  const selectableRows = props.selectableRows ?? configSelectableRows;
  // options.enableRowSelection is used as dependency in useBuiltinColumns, therefore we need to cache this function to avoid infinite rerenders
  const enableRowSelectionCallback = useCallback(
    (row: Row<TDataTableFeatures, Exclude<TData, unknown>>) => {
      // If there is a function defined that will allow for disabling certain
      // row selections, we can apply this one.
      if (
        selectableRows !== undefined &&
        typeof selectableRows !== 'boolean' &&
        selectableRows.disableRowSelection
      ) {
        return !selectableRows.disableRowSelection(row.original);
      }
      return true;
    },
    [selectableRows],
  );

  if (selectableRows !== undefined) {
    // eslint-disable-next-line react-hooks/immutability
    options.enableRowSelection = true;
    let rowSelectionMode: DataTableRowSelectionOptions<
      TDataTableFeatures,
      Row<TDataTableFeatures, Exclude<TData, unknown>>
    >['rowSelectionSelectAllMode'] = 'page';

    // If selectableRows is controlled via a boolean, we can default the options
    if (selectableRows === true || selectableRows === false) {
      // eslint-disable-next-line react-hooks/immutability
      options.enableRowSelection = selectableRows;
    } else {
      const { selectAllBehavior, limit } = selectableRows;

      // eslint-disable-next-line react-hooks/immutability
      options.enableRowSelection = enableRowSelectionCallback;

      // Change the rowSelectionMode for the _select all_ checkbox if it is defined.
      if (selectAllBehavior) {
        rowSelectionMode = selectAllBehavior;
      }

      // Defines a row selection limit for the maximum count of rows that are
      // selectable.
      // eslint-disable-next-line react-hooks/immutability
      options.rowSelectionLimit =
        limit !== undefined && limit >= 0 ? limit : undefined;
    }

    // eslint-disable-next-line react-hooks/immutability
    options.enableMultiRowSelection = true;
    // eslint-disable-next-line react-hooks/immutability
    options.rowSelectionSelectAllMode = rowSelectionMode;
  } else {
    // eslint-disable-next-line react-hooks/immutability
    options.enableRowSelection = false;
  }

  const [selectedRows, setSelectedRows] = useControllableState<
    Record<string, boolean>
  >({
    value: props.selectedRows,
    defaultValue: props.defaultSelectedRows,
    onChange: props.onRowSelectionChange,
  });

  // eslint-disable-next-line react-hooks/immutability
  options.state = {
    ...options.state,
    rowSelection: selectedRows ?? fallbackState,
  };

  // eslint-disable-next-line react-hooks/immutability
  options.onRowSelectionChange = useCallback(
    (
      updater:
        | RowSelectionState
        | ((state: RowSelectionState) => RowSelectionState),
    ) => {
      // Need to map the current Record<string, boolean | undefined> to Record<string, boolean> to
      // avoid issues with the internal state management of the table, which expects a Record<string, boolean>
      const newValue =
        typeof updater === 'function' ? updater(selectedRows ?? {}) : updater;
      for (const [key, value] of Object.entries(newValue)) {
        if (value === undefined) {
          delete newValue[key];
        }
      }
      setSelectedRows(newValue as Record<string, boolean>);
    },
    [setSelectedRows, selectedRows],
  );
}
